import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { CommonTemplateItem, CommonTemplateItemsBulkInput } from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';

interface CommonTemplateItemsResponse {
  items: CommonTemplateItem[];
}

export const commonTemplateItemKeys = {
  all: ['common-template-items'] as const,
  list: () => [...commonTemplateItemKeys.all, 'list'] as const,
};

export function useCommonTemplateItems() {
  return useQuery({
    queryKey: commonTemplateItemKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<CommonTemplateItemsResponse>('/common-template-items');
      return response.items;
    },
  });
}

export function useUpdateCommonTemplateItems() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CommonTemplateItemsBulkInput) => {
      const response = await apiClient.put<CommonTemplateItemsResponse>('/common-template-items', data);
      return response.items;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commonTemplateItemKeys.list() });
      toast({
        title: 'Common items saved',
        description: 'Common line items have been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save common line items.',
        variant: 'destructive',
      });
    },
  });
}
